import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import PageHeader from "../components/PageHeader";
import LogoSlider from "../components/LogoSlider";
import Reveal from "../components/Reveal";
import SmartImage from "../components/SmartImage";
import Seo from "../components/Seo";
import { useI18n } from "../i18n";
import { useSiteData } from "../siteData";
import { SEO_META } from "../seo";
import { assetUrl } from "../adminApi";

export default function Services() {
  const { t, loc } = useI18n();
  const { content } = useSiteData();
  const items = content.services?.items || [];
  const process = content.about?.process || [];
  const pages = content.pages || {};
  const txt = (k, f) => loc(pages[k]) || t(f);

  return (
    <div>
      <Seo path="/services" title={loc(SEO_META.services.title)} description={loc(SEO_META.services.description)} />
      <PageHeader
        index={txt("servicesIndex", "label.studio")}
        title={<span className="whitespace-pre-line">{txt("servicesTitle", "services.title")}</span>}
        lead={txt("servicesLead", "services.lead")}
      />

      <section className="shell py-20 lg:py-28">
        <div className="border-t border-[#E2E0D8]">
          {items.map((s) => (
            <Reveal key={s.no}>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 py-10 lg:py-14 border-b border-[#E2E0D8] hover:bg-[#FFFFFF] transition-colors duration-500 px-0 lg:px-6 lg:-mx-6">
                <div className="lg:col-span-1 mono text-[10px] text-[#A8A29A] pt-2">{s.no}</div>
                <div className="lg:col-span-4">
                  <h2 className="display text-[28px] lg:text-[34px] text-[#141414] leading-[1.02]">
                    {loc(s.title)}
                  </h2>
                </div>
                <div className="lg:col-span-4">
                  <p className="text-[14.5px] leading-relaxed text-[#141414]/70">{loc(s.text)}</p>
                </div>
                <div className="lg:col-span-3">
                  <ul className="space-y-2.5">
                    {loc(s.points).map((p) => (
                      <li key={p} className="flex items-start gap-2.5">
                        <Check size={13} strokeWidth={2} className="text-[#C8B29B] mt-1 shrink-0" />
                        <span className="text-[13.5px] text-[#141414]/70">{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-[#EFECE6] border-t border-b border-[#E2E0D8] py-24 lg:py-32">
        <div className="shell">
          <p className="mono text-[9.5px] text-[#6B675F] mb-6">{txt("servicesProcessLabel", "services.process")}</p>
          <h2 className="display text-[38px] sm:text-[52px] lg:text-[64px] text-[#141414] mb-16 max-w-[900px] whitespace-pre-line leading-[1.0]">
            {txt("servicesProcessTitle", "services.processTitle")}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-[#E2E0D8] border border-[#E2E0D8]">
            {process.map((p, i) => (
              <Reveal key={p.no} delay={i * 70}>
                <div className="bg-[#FFFFFF] p-7 lg:p-8 h-full">
                  <span className="mono text-[9px] text-[#A8A29A]">{p.no}</span>
                  <h3 className="text-[18px] font-medium text-[#141414] mt-8">{loc(p.title)}</h3>
                  <p className="text-[13px] text-[#141414]/60 mt-4 leading-relaxed">{loc(p.text)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <LogoSlider page="services" anchor="afterProcess" />

      <section className="shell py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 media aspect-[16/10] border border-[#E2E0D8]">
            <SmartImage
              src={assetUrl(pages.servicesProductionImage) || "https://images.unsplash.com/photo-1781032040825-04240013c228?w=1500&q=80&auto=format&fit=crop"}
              alt="CNC production studio"
              fallbackIndex={11}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <p className="mono text-[9.5px] text-[#6B675F] mb-6">{txt("servicesProductionLabel", "services.production")}</p>
            <h3 className="display text-[32px] lg:text-[40px] text-[#141414] leading-[1.04] whitespace-pre-line">
              {txt("servicesProductionTitle", "services.productionTitle")}
            </h3>
            <p className="text-[14.5px] text-[#141414]/70 mt-6 leading-relaxed">{txt("servicesProductionText", "services.productionText")}</p>
            <Link
              to="/contact"
              className="mt-9 inline-flex items-center gap-2 bg-[#141414] text-[#F7F6F2] px-6 py-4 mono text-[10px] hover:bg-[#2A2A28] transition-colors duration-300"
            >
              {txt("servicesProductionCta", "cta.sample")} <ArrowUpRight size={14} strokeWidth={1.6} />
            </Link>
          </div>
        </div>
      </section>
      <LogoSlider page="services" anchor="bottom" />
    </div>
  );
}
